/**
 * React Hook for Supabase with Clerk Authentication
 *
 * This hook provides an easy way to access an authenticated Supabase client
 * in React components.
 *
 * Features:
 * - Automatic authentication with Clerk JWT
 * - Memoized client instance (doesn't recreate on every render)
 * - Loading and error states
 * - Type-safe database queries
 *
 * Usage:
 * ```typescript
 * function MyComponent() {
 *   const { supabase, loading, error } = useSupabase();
 *
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   const { data } = await supabase.from('eulogies').select('*');
 * }
 * ```
 */

import { useState, useEffect } from 'react';
import { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '@clerk/clerk-react';
import { createSupabaseClient, Database } from '@/lib/supabaseClient';

interface UseSupabaseReturn {
  supabase: SupabaseClient<Database> | null;
  loading: boolean;
  error: Error | null;
}

/**
 * Hook to get an authenticated Supabase client
 *
 * @returns {UseSupabaseReturn} - Supabase client, loading state, and error
 */
export function useSupabase(): UseSupabaseReturn {
  const { isSignedIn, isLoaded } = useAuth();
  const [supabase, setSupabase] = useState<SupabaseClient<Database> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function initializeSupabase() {
      try {
        // Wait for Clerk to finish loading
        if (!isLoaded) {
          return;
        }

        // User must be signed in
        if (!isSignedIn) {
          throw new Error('User must be signed in to access database');
        }

        // Create authenticated Supabase client
        const client = await createSupabaseClient();

        if (isMounted) {
          setSupabase(client);
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize Supabase'));
          setLoading(false);
        }
      }
    }

    initializeSupabase();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [isSignedIn, isLoaded]);

  return { supabase, loading, error };
}

/**
 * Hook for real-time Supabase subscriptions
 *
 * Usage:
 * ```typescript
 * function EulogyEditor({ eulogyId }: { eulogyId: string }) {
 *   const [eulogy, setEulogy] = useState(null);
 *
 *   useSupabaseSubscription(
 *     'eulogies',
 *     `id=eq.${eulogyId}`,
 *     (payload) => {
 *       console.log('Eulogy updated:', payload.new);
 *       setEulogy(payload.new);
 *     }
 *   );
 *
 *   return <div>{eulogy?.content}</div>;
 * }
 * ```
 *
 * @param table - Table name to subscribe to
 * @param filter - PostgreSQL filter (e.g., 'id=eq.123')
 * @param callback - Function to call when data changes
 */
export function useSupabaseSubscription(
  table: string,
  filter: string,
  callback: (payload: any) => void
) {
  const { supabase } = useSupabase();

  useEffect(() => {
    if (!supabase) return;

    const channel = supabase
      .channel(`${table}-${filter}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
          filter: filter,
        },
        callback
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, table, filter, callback]);
}

/**
 * Hook to fetch data from Supabase with automatic refetching
 *
 * Usage:
 * ```typescript
 * function EulogyList() {
 *   const { data, loading, error, refetch } = useSupabaseQuery(
 *     async (supabase) => {
 *       return supabase.from('eulogies').select('*').order('created_at', { ascending: false });
 *     }
 *   );
 *
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error.message}</div>;
 *
 *   return (
 *     <div>
 *       {data?.map((eulogy) => <div key={eulogy.id}>{eulogy.title}</div>)}
 *       <button onClick={refetch}>Refresh</button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useSupabaseQuery<T>(
  queryFn: (supabase: SupabaseClient<Database>) => Promise<{ data: T | null; error: any }>
) {
  const { supabase } = useSupabase();
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = async () => {
    if (!supabase) return;

    setLoading(true);
    setError(null);

    try {
      const result = await queryFn(supabase);

      if (result.error) {
        throw new Error(result.error.message);
      }

      setData(result.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Query failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [supabase]);

  return { data, loading, error, refetch: fetchData };
}
